import {DevelopmentServices} from "./services/development";
import {BaseServices, IScore} from "./services/base";
import {WorkerServices} from "./services/worker";
import {EventHandler} from "@utility/eventHandler";
import {GameEvent} from "@game/gameEvent";
import {ProductionServices} from "./services/production";

interface IHostnameInitialise {
    hostnames: RegExp[]
    create: () => BaseServices
}

const SERVICES: IHostnameInitialise[] = [
    {
        hostnames: [/192.168.0.80/, /localhost/, /127.0.0.1/],
        create: () => new DevelopmentServices()
    },
    {
        hostnames: [/wordland/],
        create: () => new ProductionServices()
    },
    {
        hostnames: [/.*/],
        create: () => new WorkerServices()
    }
];

export class Host {
    events = new EventHandler<GameEvent>();
    _initialised = false;

    private services: BaseServices[] = [];

    async initialise() {
        const hostname = window.location.hostname;
        const valid = SERVICES.filter(service =>
            service.hostnames.some(regexp => regexp.exec(hostname)));

        this._initialised = false;
        for (const filtered of valid) {
            const service = filtered.create();

            try {
                if (await service.initialise()) {
                    this.services.push(service);
                } else {
                    console.error(`Failed to initialise service ${service.name}`)
                }
            }
            catch (error: any) {
                console.error(`Exception initialising service ${service.name}: ${error.message}`)
            }
        }
        this._initialised = true;
        this.events.update(new GameEvent("Initialised"));
        return this.services.length > 0;
    }

    get authenticated() {
        return this.services.some(service => service.authenticated);
    }

    get initialised() {
        return this._initialised;
    }
    
    cloud<result>(name: string, parameters?: any): Promise<result> {
        const service = this.getServices("cloud").pop();
        
        return service?.cloud(name, parameters) ?? Promise.resolve(false) as Promise<result>;
    }

    async getLeaderboards() {
        const leaderboards: Record<string, IScore[]> = {};

        for (const service of this.getServices("leaderboard")) {
            leaderboards[service.name] = await service.getLeaderboard();
        }
        return leaderboards;
    }

    getLoggedIn() {
        return this.getServices("authenticate").some(service => service.getLoggedIn());
    }

    getUserName() {
        return this.getServices("userName").find(service =>
            service.getUserName())?.getUserName() ?? null;
    }

    async linkAccounts(account: string) {
        const service = this.getService(account);

        if (service) {
            return service.linkAccounts();
        } else {
            return false;
        }
    }

    logEvent(event: string, params: any = {}) {
        this.getServices("events").forEach(service => service.logEvent(event, params));
    }

    async logIn(props: Record<string, any>) {
        const loggedIn: string[] = [];

        for (const service of this.getServices("logIn")) {
            if (await service.logIn(props)) {
                loggedIn.push(service.name);
            }
        }
        console.log(`Logged in to: ${loggedIn.join(", ")}`);
        if (loggedIn.length > 0) {
            this.events.update(new GameEvent("Authenticated", {authenticated: true}));
            return true;
        }
        return false;
    }

    async logOut(serviceName?: string) {
        if (serviceName !== undefined) {
            return this.getService(serviceName)?.logOut();
        } else {
            return Promise.all(this.getServices("logOut").map(service => service.logOut()));
        }
    }

    async resetPassword(email: string, serviceName?: string) {
        if (serviceName !== undefined) {
            return this.getService(serviceName)?.resetPassword(email);
        } else {
            return Promise.all(this.getServices("logOut").map(service =>
                service.resetPassword(email)));
        }
    }
    async signUp(serviceName: string, props: Record<string, string>) {
        const service = this.getService(serviceName);

        if (service) {
            if (await service.signUp(props)) {
                console.log(`Signed up with: ${serviceName}`);
            }
        }
    }
    submitAchievement(achievement: string) {
        this.getServices("achievement").forEach(service => service.submitAchievement(achievement));
    }

    submitScore(score: number) {
        this.getServices("score").forEach(service => service.submitScore(score));
    }

    private getService(serviceName: string) {
        return this.services.find(service => service.name === serviceName);
    }
    private getServices(api: string) {
        return this.services.filter(service => service.getSupports(api));
    }
}